import {
  CustomDisabledIcon,
  EllipsisIcon,
  PageButton,
  PaginationStyled,
} from './Pagination.styles';
import { useEffect, useState } from 'react';
interface Props {
  numPages: number;
  initialPage?: number;
  'data-testid'?: string;
  handleClickPage: (index: number) => void;
  currentPageNumber?: number;
}

const PAGE_TRIGGER = 7;
const ELLIPSIS_TRIGGER = 5;

export const Pagination = ({ numPages, handleClickPage, ...props }: Props) => {
  const pages = [...Array(numPages).keys()].map((i) => i + 1);
  const [currentPage, setCurrentPage] = useState(props.initialPage ?? 1);
  const [hover, setHover] = useState(-1);

  useEffect(() => {
    if (props.currentPageNumber) {
      setCurrentPage(props.currentPageNumber);
      setHover(-1);
    }
  }, [props.currentPageNumber]);

  if (numPages < PAGE_TRIGGER) {
    return (
      <PaginationStyled {...props}>
        {pages.map((page) =>
          page === currentPage ? (
            <PageButton
              pageNumber={page}
              key={page}
              size={'medium'}
              variant={'secondary'}
              data-testid={`page-${page}`}
            >
              {page}
            </PageButton>
          ) : (
            <PageButton
              pageNumber={page}
              key={page}
              size={'medium'}
              variant={hover === page ? 'secondary' : 'tertiary'}
              onClick={() => {
                setCurrentPage(page);
                handleClickPage(page);
              }}
              onMouseOver={() => setHover(page)}
              onMouseOut={() => setHover(-1)}
              data-testid={`page-${page}`}
            >
              {page}
            </PageButton>
          ),
        )}
      </PaginationStyled>
    );
  }
  if (numPages === PAGE_TRIGGER) {
    return (
      <PaginationStyled {...props}>
        <CustomDisabledIcon
          key={'back'}
          size={'small'}
          variant={'tertiary'}
          iconName={'uil uil-angle-left'}
          disabled={currentPage === 1}
          onClick={() => {
            if (currentPage > 1) {
              setCurrentPage(currentPage - 1);
              handleClickPage(currentPage - 1);
            }
          }}
        />
        {pages.map((page) =>
          page === currentPage ? (
            <PageButton
              pageNumber={page}
              key={page}
              size={'medium'}
              variant={'secondary'}
              data-testid={`page-${page}`}
            >
              {page}
            </PageButton>
          ) : (
            <PageButton
              pageNumber={page}
              key={page}
              size={'medium'}
              variant={hover === page ? 'secondary' : 'tertiary'}
              onClick={() => {
                setCurrentPage(page);
                handleClickPage(page);
              }}
              onMouseOver={() => setHover(page)}
              onMouseOut={() => setHover(-1)}
              data-testid={`page-${page}`}
            >
              {page}
            </PageButton>
          ),
        )}
        <CustomDisabledIcon
          key={'forward'}
          size={'small'}
          variant={'tertiary'}
          iconName={'uil uil-angle-right'}
          disabled={currentPage === numPages}
          onClick={() => {
            if (currentPage < numPages) {
              setCurrentPage(currentPage + 1);
              handleClickPage(currentPage + 1);
            }
          }}
        />
      </PaginationStyled>
    );
  }
  return (
    <PaginationStyled {...props}>
      <CustomDisabledIcon
        key={'back'}
        size={'small'}
        variant={'tertiary'}
        iconName={'uil uil-angle-left'}
        disabled={currentPage === 1}
        onClick={() => {
          if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
            handleClickPage(currentPage - 1);
          }
        }}
      />
      {pages.map((page) => {
        if (page === currentPage) {
          return (
            <PageButton
              pageNumber={page}
              key={page}
              size={'medium'}
              variant={'secondary'}
              data-testid={`page-${page}`}
            >
              {page}
            </PageButton>
          );
        }

        const halfway = Math.floor(numPages / 2);
        const selectedPageIsBeforeTrigger = currentPage < ELLIPSIS_TRIGGER;
        const currentPosIsMid = page === halfway;
        const secondPage = page === 2;
        const showFirstEllipsis =
          (selectedPageIsBeforeTrigger && currentPosIsMid) ||
          (!selectedPageIsBeforeTrigger && secondPage);

        if (showFirstEllipsis) {
          return (
            <EllipsisIcon
              size={'small'}
              key={page}
              variant={'tertiary'}
              disabled={true}
              iconName={'uil uil-ellipsis-h'}
              data-testid={`${props['data-testid']}-first-ellipsis`}
            />
          );
        }

        const selectedPageIsAfterPriorToTrigger =
          currentPage > ELLIPSIS_TRIGGER - 1;
        const END_CUTOFF = numPages - 2;
        const currentPageIsBeforeEndCutoff = currentPage < END_CUTOFF;
        const atPriorToEndPosition = page === END_CUTOFF + 1;
        const showSecondEllipsis =
          selectedPageIsAfterPriorToTrigger &&
          currentPageIsBeforeEndCutoff &&
          atPriorToEndPosition;

        if (showSecondEllipsis) {
          return (
            <EllipsisIcon
              key={page}
              size={'small'}
              variant={'tertiary'}
              disabled={true}
              iconName={'uil uil-ellipsis-h'}
              data-testid={`${props['data-testid']}-second-ellipsis`}
            />
          );
        }

        if (
          currentPage <= ELLIPSIS_TRIGGER &&
          page > ELLIPSIS_TRIGGER &&
          page < numPages
        ) {
          return null;
        }

        const END_RANGE = END_CUTOFF - 2;
        const notAdjacentToCurrentPageLeft =
          page < currentPage - 1 && page > 2 && page < END_RANGE;
        const notAdjacentToCurrentPageRight =
          page > currentPage + 1 && page < numPages;
        if (
          currentPage > ELLIPSIS_TRIGGER &&
          (notAdjacentToCurrentPageLeft || notAdjacentToCurrentPageRight)
        ) {
          return null;
        }

        return (
          <PageButton
            pageNumber={page}
            key={page}
            size={'medium'}
            variant={hover === page ? 'secondary' : 'tertiary'}
            onClick={() => {
              setCurrentPage(page);
              handleClickPage(page);
            }}
            onMouseOver={() => setHover(page)}
            onMouseOut={() => setHover(-1)}
            data-testid={`page-${page}`}
          >
            {page}
          </PageButton>
        );
      })}
      <CustomDisabledIcon
        key={'forward'}
        size={'small'}
        variant={'tertiary'}
        iconName={'uil uil-angle-right'}
        disabled={currentPage === numPages}
        onClick={() => {
          if (currentPage < numPages) {
            setCurrentPage(currentPage + 1);
            handleClickPage(currentPage + 1);
          }
        }}
      />
    </PaginationStyled>
  );
};
